import { HttpClient, HttpParams } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Project } from '../../../../core/models/project.model';
import { LanguageService } from '../../../../core/services/language.service';
import { ProjectsCard } from '../../../../shared/components/projects-card/projects-card';
import { environment } from '../../../../../environments/environment';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

interface ProjectSearchApiResponse {
  message: string;
  results: {
    data: ProjectSearchItem[];
    pages: number;
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    nextPage: number | null;
    previousPage: number | null;
  };
}

interface ProjectSearchItem {
  _id?: string;
  entityType?: string;
  type?: string;
  project?: {
    name?: string;
    type?: string | string[];
    priceStart?: number | string;
    deliveryDate?: string;
    plannedUnits?: number | string;
    location?: string;
  };
  profile?: {
    name?: string;
  };
  developer?: {
    name?: string;
  };
  owner?: {
    name?: string;
  };
  thumbnail?: string | { secure_url?: string };
}

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [CommonModule, ProjectsCard, TranslatePipe],
  templateUrl: './projects.html',
  styleUrl: './projects.scss',
})
export class Projects implements OnInit, OnChanges {
  @Input() searchQuery = '';
  @Input() searchVersion = 0;

  isLoading = false;
  errorMessage = '';

  private readonly baseUrl = environment.api.baseUrl;
  private readonly fallbackImage = '';
  projects: Project[] = [];

  constructor(
    private router: Router,
    private http: HttpClient,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchVersion'] && !changes['searchVersion'].firstChange) {
      this.loadProjects();
    }
  }

  retrySearch(): void {
    this.loadProjects();
  }

  onProjectClick(project: Project): void {
    this.router.navigate(['/projects', project.id], {
      state: { property: project },
    });
  }

  private loadProjects(): void {
    const query = this.searchQuery.trim();
    const currentLanguage = this.languageService.getCurrentLanguage();

    const headers = {
      'Accept-Language': currentLanguage,
      'Content-Language': currentLanguage,
      'X-Language': currentLanguage,
    };

    let params = new HttpParams()
      .set('page', '1')
      .set('limit', '10');
      
    if (query) {
      params = params.set('name', query);
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.http
      .get<any>(`${this.baseUrl}/public/search/projects-and-profiles?targetType=project`, {
        params,
        headers,
      })
      .subscribe({
        next: (response) => {
          const data = Array.isArray(response) ? response : response?.results?.data || [];
          const mappedProjects = this.mapApiResults(data);
          this.projects = mappedProjects;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'Failed to load projects. Please try again.';
          this.projects = [];
        },
      });
  }

  private mapApiResults(items: any[]): Project[] {
    return items.map((item) => {
      // If the item has the old structure with 'project' nested, use that, otherwise use the item itself
      const isOldStructure = !!item?.project;
      const projectData = isOldStructure ? item.project : item;
      
      let rawImage = this.fallbackImage;
      if (isOldStructure) {
        rawImage = this.resolveThumbnail(item);
      } else if (item.gallery && item.gallery.length > 0) {
        rawImage = item.gallery[0].secure_url;
      } else if (item.masterPlan && item.masterPlan.secure_url) {
        rawImage = item.masterPlan.secure_url;
      }

      return {
        id: String(item?.targetId || item?._id || ''),
        name: projectData.name || projectData.name_en || projectData.name_ar || '',
        developer: isOldStructure ? this.resolveDeveloperName(item) : '',
        location: projectData.location || projectData.location_en || projectData.location_ar || '',
        imageUrl: rawImage,
        deliveryDate: this.formatDeliveryDate(projectData.expectedDeliveryDate || projectData.deliveryDate),
        unitTypes: this.normalizeUnitTypes(projectData.projectType || projectData.type),
        startingPrice: this.toNumber(projectData.price?.salePrice?.basePrice || projectData.salePrice?.basePrice || projectData.priceStart),
        totalUnits: this.toNumber(projectData.plannedUnits || projectData.totalUnits),
        currency: item?.currency || 'EGP',
        isFavorite: item?.isFavorite || false,
        isFeatured: item?.isFeatured || false,
        isTrusted: item?.isTrusted || false,
        rating: item?.rating || 0,
        totalReviews: item?.totalReviews || 0,
      };
    });
  }

  private resolveDeveloperName(item: ProjectSearchItem): string {
    return item?.developer?.name || item?.owner?.name || item?.profile?.name || '';
  }

  private resolveThumbnail(item: ProjectSearchItem): string {
    const thumb = item?.thumbnail;
    if (typeof thumb === 'string' && thumb.trim()) return thumb.trim();
    if (thumb && typeof thumb === 'object' && thumb.secure_url) return thumb.secure_url;
    return this.fallbackImage;
  }

  private normalizeUnitTypes(value: string | string[] | undefined): string[] {
    if (Array.isArray(value)) {
      const list = value.map((item) => String(item).trim()).filter(Boolean);
      return list.length ? list : ['Project'];
    }

    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }

    return ['Project'];
  }

  private formatDeliveryDate(value: string | undefined): string {
    if (!value) return 'TBD';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return String(date.getFullYear());
  }

  private toNumber(value: number | string | undefined): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value.replace(/[^\d.-]/g, ''));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return 0;
  }
}
